import React, { useState, useEffect, useRef } from "react";
import { FaFileExcel, FaArrowLeft, FaSearch, FaDownload, FaSync } from "react-icons/fa";
import * as XLSX from "xlsx";
import axios from "axios";
import EnhancedFinancialTable from "./EnhancedFinancialTable/index";
import CONFIG from "../config";

const DataContent = ({
  searchQuery,
  setSearchQuery,
  extractedData,
  fullscreenMode,
  selectedCompany: propSelectedCompany,
  showBackArrow = false,
  onBackClick,
  uploadedExcelData,
  setUploadedExcelData,
  handleUploadedExcelData,
  activeStatement: propActiveStatement,
  onExcelDownload,
  onDownloadComplete
}) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [periodType, setPeriodType] = useState("Annual");
  const [activeStatement, setActiveStatement] = useState(propActiveStatement || "Income Statement");
  const [financialData, setFinancialData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [customExcelData, setCustomExcelData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [isFetchingBackendData, setIsFetchingBackendData] = useState(false);
  const [excelFiles, setExcelFiles] = useState({});
  const [companyList, setCompanyList] = useState([]);
  const searchResultsRef = useRef(null);

  // Get auth headers using CONFIG helper
  const getAuthHeaders = () => CONFIG.getAuthHeaders();

  /* ---------------- CLICK OUTSIDE TO CLOSE DROPDOWN ---------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /* ---------------- INITIALIZE DATA ---------------- */
  useEffect(() => {
    console.log('DataContent useEffect triggered:', { 
      extractedData, 
      activeStatement: propActiveStatement,
      uploadedExcelData 
    });

    // If we have backend extraction data
    if (extractedData?.isBackendData && extractedData.jobId) {
      console.log('Loading backend extracted data:', extractedData);
      loadBackendExtractedData(extractedData);
      return;
    }

    // If we have uploaded Excel data
    if (uploadedExcelData && uploadedExcelData.data) {
      console.log('Loading uploaded Excel data:', uploadedExcelData);
      handleUploadedExcelData(uploadedExcelData);
      return;
    }

    // If we have a selected company from prop
    if (propSelectedCompany) {
      console.log('Setting selected company from prop:', propSelectedCompany);
      setSelectedCompany(propSelectedCompany);
      if (propSelectedCompany.isBackendCompany) {
        fetchExcelFilesForCompany(propSelectedCompany.name);
      }
    }

    // If we have a statement from prop
    if (propActiveStatement) {
      setActiveStatement(propActiveStatement);
    }
  }, [extractedData, uploadedExcelData, propSelectedCompany, propActiveStatement]);

  /* ---------------- SEARCH FOR COMPANIES ---------------- */
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      setShowSearchResults(false);
      setFilteredCompanies([]);
      return;
    }

    // Don't search if we already have a selected company matching the search
    if (selectedCompany && `${selectedCompany.name} (${selectedCompany.ticker})` === searchQuery) {
      setShowSearchResults(false);
      setFilteredCompanies([]);
      return;
    }

    // Search companies function
    const searchCompanies = async () => {
      try {
        console.log('Searching for companies with query:', searchQuery);
        
        // Use CONFIG helper to get full URL
        const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
        console.log('Search URL:', searchUrl);
        
        const response = await axios.get(
          searchUrl,
          {
            headers: getAuthHeaders(),
            params: {
              company_name: searchQuery,
              page: 1,
              page_size: 20
            }
          }
        );
        
        console.log('Search response status:', response.status);
        console.log('Search response data:', response.data);
        
        // Handle different response structures
        let companiesData = [];
        
        if (response.data && response.data.files) {
          companiesData = response.data.files;
          console.log('Found files in response.data.files:', companiesData.length);
        } else if (Array.isArray(response.data)) {
          companiesData = response.data;
          console.log('Response.data is an array:', companiesData.length);
        } else if (response.data && response.data.companies) {
          companiesData = response.data.companies;
          console.log('Found companies in response.data.companies:', companiesData.length);
        }
        
        if (companiesData.length > 0) {
          console.log('Companies data to transform:', companiesData);
          
          // Transform the response to match frontend format
          const companies = companiesData.map((file, index) => {
            const companyName = file.company_name || file.name || `Company ${index + 1}`;
            return {
              id: file.company_id || file.id || `temp-${Date.now()}-${index}`,
              name: companyName,
              ticker: file.ticker || file.ticker_id || companyName.substring(0, 3).toUpperCase(),
              sector: file.sector || "Financial Services",
              isBackendCompany: true,
              hasIncomeStatement: file.hasIncomeStatement || false,
              hasBalanceSheet: file.hasBalanceSheet || false,
              hasCashFlow: file.hasCashFlow || false,
              companyId: file.company_id || file.id,
              latestYear: file.latest_year,
              downloadUrls: file.download_urls,
              statement_data: file.statement_data
            };
          });
          
          console.log('Transformed companies:', companies);
          
          setCompanyList(companies);
          setFilteredCompanies(companies);
          setShowSearchResults(true);
          
        } else {
          console.log('No companies found in the response');
          setFilteredCompanies([]);
          setShowSearchResults(false);
        }
      } catch (error) {
        console.error('Error searching companies:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setFilteredCompanies([]);
        setShowSearchResults(false);
      }
    };

    // Debounce search
    const timer = setTimeout(() => {
      searchCompanies();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  /* ---------------- LOAD BACKEND EXTRACTED DATA ---------------- */
  const loadBackendExtractedData = async (extractionData) => {
    console.log('loadBackendExtractedData called:', extractionData);
    if (!extractionData.companyName) {
      console.error('No company name in extraction data');
      return;
    }
    
    setIsFetchingBackendData(true);
    
    try {
      // Create company object
      const companyData = {
        id: Date.now().toString(),
        name: extractionData.companyName,
        ticker: extractionData.tickerId || extractionData.companyName.replace(/\s+/g, '').toUpperCase(),
        sector: "Financial Services",
        isBackendCompany: true,
        jobId: extractionData.jobId,
        statementId: extractionData.statementId
      };
      
      setSelectedCompany(companyData);
      setSearchQuery(`${extractionData.companyName} (${companyData.ticker})`);
      setShowSearchResults(false);
      
      // First, get all Excel files for this company
      await fetchExcelFilesForCompany(extractionData.companyName);
      
      // Then load the current statement data
      await loadStatementData(extractionData.companyName, activeStatement);
      
    } catch (error) {
      console.error('Error loading backend data:', error);
      alert('Failed to load extracted data from backend');
    } finally {
      setIsFetchingBackendData(false);
    }
  };

  /* ---------------- FETCH EXCEL FILES FOR COMPANY ---------------- */
  const fetchExcelFilesForCompany = async (companyName) => {
    try {
      console.log('Fetching Excel files for company:', companyName);
      
      const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
      const response = await axios.get(
        searchUrl,
        {
          headers: getAuthHeaders(),
          params: {
            company_name: companyName,
            page: 1,
            page_size: 100
          }
        }
      );
      
      console.log('Excel search response:', response.data);
      
      if (response.data.files && response.data.files.length > 0) {
        // Group Excel files by statement type
        const filesByType = {
          "Income Statement": null,
          "Balance Sheet": null,
          "Cash Flow": null
        };
        
        response.data.files.forEach(file => {
          const statementType = file.statement_type_display || 
                               file.statement_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          // Map to our frontend statement types
          if (statementType && (statementType.includes('Income') || statementType.includes('income'))) {
            filesByType["Income Statement"] = file;
          } else if (statementType && (statementType.includes('Balance') || statementType.includes('balance'))) {
            filesByType["Balance Sheet"] = file;
          } else if (statementType && (statementType.includes('Cash') || statementType.includes('cash'))) {
            filesByType["Cash Flow"] = file;
          }
        });
        
        setExcelFiles(filesByType);
        console.log('Excel files by type:', filesByType);
      } else {
        console.log('No Excel files found for company:', companyName);
        setExcelFiles({});
      }
      
    } catch (error) {
      console.error('Error fetching Excel files:', error);
      setExcelFiles({});
    }
  };

  /* ---------------- LOAD STATEMENT DATA ---------------- */
  const loadStatementData = async (companyName, statementType) => {
  console.log(`Loading ${statementType} data for ${companyName}`);
  
  setIsLoading(true);
  
  try {
    // First, search for the company to get statement IDs
    const searchUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.SEARCH);
    const searchResponse = await axios.get(
      searchUrl,
      {
        headers: getAuthHeaders(),
        params: {
          company_name: companyName,
          page: 1,
          page_size: 10
        }
      }
    );
    
    console.log(`Search response for ${companyName}:`, searchResponse.data);
    
    if (searchResponse.data.files && searchResponse.data.files.length > 0) {
      const companyData = searchResponse.data.files[0];
      
      // Map frontend statement type to backend type
      const statementTypeMap = {
        "Income Statement": "income_statement",
        "Balance Sheet": "balance_sheet", 
        "Cash Flow": "cash_flow"
      };
      
      const backendType = statementTypeMap[statementType];
      const statementInfo = companyData.statement_data?.[backendType];
      
      console.log(`Statement info for ${statementType}:`, statementInfo);
      
      if (statementInfo && statementInfo.statement_id) {
        // Get Excel data from backend
        const dataUrl = CONFIG.getFullUrl(CONFIG.API.EXCEL.STATEMENT_DATA(statementInfo.statement_id));
        const dataResponse = await axios.get(
          dataUrl,
          {
            headers: getAuthHeaders()
          }
        );
        
        console.log(`Backend data response for ${statementType}:`, dataResponse.data);
        
        if (dataResponse.data.success && dataResponse.data.data) {
          const jsonData = dataResponse.data.data;
          
          console.log(`Raw JSON data (first 3 rows):`, jsonData.slice(0, 3));
          console.log(`Total rows:`, jsonData.length);
          
          // Format the data for EnhancedFinancialTable WITH COMPANY NAME ONLY
          const formattedData = formatDataForEnhancedTable(jsonData, companyName, activeStatement);
          
          console.log(`Formatted data (first 3 rows):`, formattedData.slice(0, 3));
          console.log(`Total formatted rows:`, formattedData.length);
          
          if (formattedData && formattedData.length > 0) {
            setFinancialData(formattedData);
            
            // Extract years from data
            const years = extractYearsFromData(formattedData);
            setAvailableYears(years);
            
            // Store download info
            setExcelFiles(prev => ({
              ...prev,
              [statementType]: {
                id: statementInfo.statement_id,
                jobId: statementInfo.job_id,
                downloadUrl: dataResponse.data.download_url,
                fileExists: dataResponse.data.file_exists
              }
            }));
            
            console.log(`✅ Successfully loaded ${formattedData.length} rows for ${statementType}`);
          } else {
            console.log(`❌ Formatted data is empty for ${statementType}`);
            setFinancialData([]);
            setAvailableYears([]);
          }
          
        } else {
          console.log(`❌ No data in backend response for ${statementType}`);
          console.log(`Response:`, dataResponse.data);
          setFinancialData([]);
          setAvailableYears([]);
        }
        
      } else {
        console.log(`❌ No statement ID found for ${statementType}`);
        console.log(`Company data:`, companyData);
        setFinancialData([]);
        setAvailableYears([]);
      }
      
    } else {
      console.log(`❌ No company found: ${companyName}`);
      setFinancialData([]);
      setAvailableYears([]);
    }
    
  } catch (error) {
    console.error(`❌ Error loading ${statementType} data:`, error);
    
    if (error.response) {
      console.error(`Response status:`, error.response.status);
      console.error(`Response data:`, error.response.data);
    }
    
    setFinancialData([]);
    setAvailableYears([]);
  } finally {
    setIsLoading(false);
  }
};

  /* ---------------- FORMAT DATA FOR ENHANCED TABLE ---------------- */
  /* ---------------- FORMAT DATA FOR ENHANCED TABLE ---------------- */
/* ---------------- FORMAT DATA FOR ENHANCED TABLE ---------------- */
const formatDataForEnhancedTable = (jsonData, companyName, statementType) => {
  if (!jsonData || !Array.isArray(jsonData) || jsonData.length === 0) {
    console.log('formatDataForEnhancedTable: No data or invalid input');
    return [];
  }
  
  console.log('Formatting data with columns:', Object.keys(jsonData[0]));
  console.log('First few rows of jsonData:', jsonData.slice(0, 5));
  
  // Get all unique column names
  const allColumns = new Set();
  if (jsonData.length > 0) {
    Object.keys(jsonData[0]).forEach(key => allColumns.add(key));
  }
  
  const columns = Array.from(allColumns);
  console.log('All columns found:', columns);
  
  // Check what's in the first few rows of the data
  const firstRowValue = jsonData[0]?.[columns[0]] || '';
  const secondRowValue = jsonData[1]?.[columns[0]] || '';
  
  console.log('First row value:', firstRowValue);
  console.log('Second row value:', secondRowValue);
  
  // Create Row 1: Company name only (in first column)
  const companyRow = {};
  columns.forEach((header, index) => {
    if (index === 0) {
      companyRow[header] = companyName || "";
    } else {
      companyRow[header] = "";
    }
  });
  
  // Check if the data already contains statement name in first row
  // If first row contains statement name, we need to handle differently
  const firstRowLower = String(firstRowValue).toLowerCase();
  const statementTypeLower = String(statementType).toLowerCase();
  
  let formattedData;
  
  if (firstRowLower.includes('statement') || firstRowLower.includes(statementTypeLower)) {
    console.log('Data already contains statement name in first row');
    
    // Data structure: Row 1 = Statement name, Row 2 = Empty, Row 3 = Description header
    // We need to insert company name at the beginning
    
    // Clean and format the data
    const cleanedData = jsonData.map((row, rowIndex) => {
      const formattedRow = {};
      
      columns.forEach((header, colIndex) => {
        let value = row[header];
        
        // Handle null/undefined
        if (value === null || value === undefined || value === '') {
          value = "";
        }
        
        // Convert to string
        let stringValue = String(value).trim();
        
        // Format years in header row (third row, index 2)
        if (rowIndex === 2 && colIndex > 0 && stringValue) {
          // Check if it's a year like "2024" or "FY 2024"
          const yearMatch = stringValue.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            // Extract just the year and format as "FY 2024"
            formattedRow[header] = `FY ${yearMatch[0]}`;
            return;
          }
        }
        
        // Handle numeric values for data rows (not first 3 rows)
        if (rowIndex > 2 && colIndex > 0 && stringValue !== '') {
          // Remove commas, currency symbols, etc.
          const cleaned = stringValue.replace(/[$,%\s()]/g, '');
          
          // Check if it's a number
          if (cleaned !== '' && !isNaN(cleaned)) {
            const numValue = parseFloat(cleaned);
            
            // Check if original had parentheses (negative)
            if (stringValue.includes('(') && stringValue.includes(')')) {
              formattedRow[header] = -Math.abs(numValue);
              return;
            } else {
              formattedRow[header] = numValue;
              return;
            }
          }
        }
        
        formattedRow[header] = value;
      });
      
      return formattedRow;
    });
    
    // Insert company name at the beginning
    formattedData = [companyRow, ...cleanedData];
    
  } else {
    console.log('Creating standard structure');
    
    // Create Row 2: Statement type only (in first column) - UPPERCASE
    const statementRow = {};
    columns.forEach((header, index) => {
      if (index === 0) {
        statementRow[header] = (statementType || activeStatement || "").toUpperCase();
      } else {
        statementRow[header] = "";
      }
    });
    
    // Create Row 3: Empty row (all cells empty)
    const emptyRow = {};
    columns.forEach((header) => {
      emptyRow[header] = "";
    });
    
    // Clean and format the data
    const cleanedData = jsonData.map((row, rowIndex) => {
      const formattedRow = {};
      
      columns.forEach((header, colIndex) => {
        let value = row[header];
        
        // Handle null/undefined
        if (value === null || value === undefined || value === '') {
          value = "";
        }
        
        // Convert to string
        let stringValue = String(value).trim();
        
        // For the first row of data, format years in year columns
        if (rowIndex === 0 && colIndex > 0 && stringValue) {
          // Check if it's a year like "2024" or "FY 2024"
          const yearMatch = stringValue.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            // Extract just the year and format as "FY 2024"
            formattedRow[header] = `FY ${yearMatch[0]}`;
            return;
          }
        }
        
        // Handle numeric values for data rows
        if (rowIndex > 0 && colIndex > 0 && stringValue !== '') {
          // Remove commas, currency symbols, etc.
          const cleaned = stringValue.replace(/[$,%\s()]/g, '');
          
          // Check if it's a number
          if (cleaned !== '' && !isNaN(cleaned)) {
            const numValue = parseFloat(cleaned);
            
            // Check if original had parentheses (negative)
            if (stringValue.includes('(') && stringValue.includes(')')) {
              formattedRow[header] = -Math.abs(numValue);
              return;
            } else {
              formattedRow[header] = numValue;
              return;
            }
          }
        }
        
        formattedRow[header] = value;
      });
      
      return formattedRow;
    });
    
    // Combine all rows
    formattedData = [
      companyRow,      // Row 1: Company name
      statementRow,    // Row 2: Statement type
      emptyRow,        // Row 3: Empty row
      ...cleanedData   // Row 4+: Description header + Actual financial data
    ];
  }
  
  console.log(`Formatted ${formattedData.length} rows (from ${jsonData.length} original rows)`);
  
  if (formattedData.length > 0) {
    console.log('Row 1 (Company):', formattedData[0]);
    console.log('Row 2:', formattedData[1]);
    console.log('Row 3:', formattedData[2]);
    console.log('Row 4:', formattedData[3]);
    if (formattedData.length > 4) {
      console.log('Row 5:', formattedData[4]);
    }
  }
  
  return formattedData;
};

  /* ---------------- EXTRACT YEARS FROM DATA ---------------- */
  const extractYearsFromData = (data) => {
    if (!data || data.length === 0) return [];
    
    const years = new Set();
    
    // Look for year patterns in headers
    if (data.length > 0) {
      const firstRow = data[0];
      Object.keys(firstRow).forEach(key => {
        // Match 4-digit years
        const yearMatch = key.match(/\b(19|20)\d{2}\b/);
        if (yearMatch) {
          years.add(yearMatch[0]);
        }
      });
    }
    
    return Array.from(years).sort();
  };

  /* ---------------- HANDLE STATEMENT CHANGE ---------------- */
  useEffect(() => {
    console.log('Statement changed to:', activeStatement);
    
    if (selectedCompany?.isBackendCompany && selectedCompany.name) {
      loadStatementData(selectedCompany.name, activeStatement);
    } else if (selectedCompany && uploadedExcelData?.data) {
      // Handle uploaded Excel data WITH COMPANY NAME ONLY
      const formattedData = formatDataForEnhancedTable(uploadedExcelData.data, selectedCompany.name || "", activeStatement);
      
      const years = extractYearsFromData(formattedData);
      setAvailableYears(years);
    }
  }, [activeStatement, selectedCompany]);

  /* ---------------- SELECT COMPANY ---------------- */
  const handleSelectCompany = (company) => {
    console.log('Selecting company:', company);
    setSelectedCompany(company);
    setSearchQuery(`${company.name} (${company.ticker})`);
    setShowSearchResults(false);
    
    // Fetch Excel files for this company
    fetchExcelFilesForCompany(company.name);
    
    // Load data for the active statement
    loadStatementData(company.name, activeStatement);
  };

  /* ---------------- REFRESH DATA ---------------- */
  const handleRefreshData = () => {
    if (selectedCompany?.isBackendCompany && selectedCompany.name) {
      fetchExcelFilesForCompany(selectedCompany.name);
      loadStatementData(selectedCompany.name, activeStatement);
    }
  };

  const handleExcelDownload = async () => {
    if (!financialData || financialData.length === 0) {
      alert('No data to download');
      return;
    }
    
    try {
      // Get the Excel file for current statement type
      const currentExcelFile = excelFiles[activeStatement];
      
      if (currentExcelFile && currentExcelFile.downloadUrl) {
        // Use the download URL from the statement data endpoint
        window.open(currentExcelFile.downloadUrl, '_blank');
        
        if (onDownloadComplete) {
          onDownloadComplete(true, `${selectedCompany.name}_${activeStatement.replace(' ', '_')}.xlsx`);
        }
      } else {
        alert('No Excel file available for download');
      }
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download Excel file');
      
      if (onDownloadComplete) {
        onDownloadComplete(false, null);
      }
    }
  };

  /* ---------------- CHECK IF EXCEL FILE EXISTS FOR STATEMENT ---------------- */
  const hasExcelFileForStatement = (statement) => {
    return excelFiles[statement] !== null && excelFiles[statement] !== undefined;
  };

  /* ---------------- PREPARE COLUMNS FOR ENHANCED TABLE ---------------- */
  const prepareColumns = () => {
    if (financialData.length === 0) {
      return [
        { id: "particular", label: "Particular", width: 180 },
        { id: "year2022", label: "FY 2022", width: 120 },
        { id: "year2023", label: "FY 2023", width: 120 },
        { id: "year2024", label: "FY 2024", width: 120 }
      ];
    }
    
    const headers = Object.keys(financialData[0]);
    return headers.map((header, index) => {
      let width = 120;
      
      // Extract year from header for better labeling
      let label = header;
      const yearMatch = header.match(/\b(19|20)\d{2}\b/);
      
      if (yearMatch) {
        label = `FY ${yearMatch[0]}`;
      } else if (index === 0) {
        label = "Particular";
        width = 180;
      } else if (header.toLowerCase().includes('description')) {
        label = "Description";
      }
      
      return {
        id: header,
        label: label,
        width: width
      };
    });
  };

  return (
    <div className={`${fullscreenMode ? "h-screen overflow-hidden" : "min-h-screen"} bg-white dark:bg-gray-800`}>
      
      {/* SEARCH RESULTS DROPDOWN */}
      {showSearchResults && filteredCompanies.length > 0 && (
        <div 
          ref={searchResultsRef}
          className="absolute top-16 left-1/2 transform -translate-x-1/2 z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-xl rounded-lg w-96 max-h-80 overflow-y-auto"
        >
          <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Search Results ({filteredCompanies.length})
              </p>
              <button
                onClick={() => setShowSearchResults(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredCompanies.map((company) => (
              <div 
                key={company.id}
                onClick={() => handleSelectCompany(company)}
                className="px-4 py-3 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {company.name}
                    </p>
                    <div className="flex items-center mt-1 space-x-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Ticker: {company.ticker}
                      </p>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                        Backend
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    {company.hasIncomeStatement && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-0.5 rounded">
                        IS
                      </span>
                    )}
                    {company.hasBalanceSheet && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                        BS
                      </span>
                    )}
                    {company.hasCashFlow && (
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded">
                        CF
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Click on a company to view financial statements
            </p>
          </div>
        </div>
      )}
      
      {/* COMPANY HEADER */}
      {selectedCompany && (
        <div className="px-6 pt-3 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Back Arrow */}
              {showBackArrow && (
                <button
                  onClick={onBackClick}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 
                           hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mr-2"
                  title="Back to Upload"
                >
                  <FaArrowLeft className="text-lg" />
                </button>
              )}
              
              {/* Company Info */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedCompany.name}{" "}
                  <span className="text-blue-600">
                    ({selectedCompany.ticker})
                  </span>
                 
                </h2>
                
              </div>

              {/* Period Toggle */}
              {selectedCompany.isBackendCompany && availableYears.length > 0 && (
                <div className="inline-flex border rounded-md overflow-hidden">
                  {['Annual', 'Quarterly'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriodType(p)}
                      className={`px-3 py-1 text-xs font-medium ${
                        periodType === p
                          ? "bg-blue-600 text-white"
                          : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

           
          </div>
        </div>
      )}

      {/* STATEMENT TABS */}
      {selectedCompany && (
        <div className="px-6 mt-2">
          <div className="inline-flex gap-1 bg-yellow-50 dark:bg-yellow-900/20">
            {["Income Statement", "Balance Sheet", "Cash Flow"].map((s) => (
              <button
                key={s}
                onClick={() => setActiveStatement(s)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors border border-yellow-300 dark:border-yellow-700
                  ${
                    activeStatement === s
                      ? "bg-white dark:bg-gray-800 text-yellow-700 dark:text-yellow-300 shadow-sm"
                      : "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
                  }`}
              >
                {s}
                {hasExcelFileForStatement(s) && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* DATA TABLE - USING ENHANCED FINANCIAL TABLE */}
      {selectedCompany && activeStatement && (
        <div className={`${fullscreenMode ? "h-[calc(100vh-170px)] overflow-auto px-6 mt-2" : "px-6 py-3"}`}>
          {isLoading || isFetchingBackendData ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading {activeStatement.toLowerCase()} data...
              </span>
            </div>
          ) : financialData.length > 0 ? (
            <EnhancedFinancialTable
    key={`${selectedCompany.id}-${activeStatement}`}
    financialData={financialData}
    onDataUpdate={setFinancialData}
    isLoading={false}
    initialColumns={prepareColumns()}
    statementId={excelFiles[activeStatement]?.id || extractedData?.statementId}
    companyName={selectedCompany?.name || ""}
    activeStatement={activeStatement}
    onRefreshData={() => loadStatementData(selectedCompany.name, activeStatement)} // ADD THIS
  />
          ) : (
            <div className="flex flex-col items-center justify-center h-60 text-gray-500 dark:text-gray-400">
              <FaFileExcel className="text-4xl mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                No {activeStatement} Data Available
              </p>
              <p className="text-sm">
                {hasExcelFileForStatement(activeStatement) 
                  ? "Excel file exists but data could not be loaded"
                  : "No Excel file found for this statement"}
              </p>
              {selectedCompany.isBackendCompany && (
                <button
                  onClick={handleRefreshData}
                  className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Try Refreshing Data
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataContent;